import React, { useState, useEffect, forwardRef, useCallback, useMemo } from "react";
import _ from "lodash";
import { Form } from "antd";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import Vocabulary from 'components/Vocabulary';
import SubProductEnvironmentTypeAPI from "restAPI/SubProductEnvironmentTypeAPI";
import { FieldTypes } from "utils/formUtils";
import message from "components/CustomMessage";
import BaseModalForm from "components/BaseModalForm";
import RepositoryAPI from 'restAPI/RepositoryAPI';
import EnvironmentName from "views/GlobalSettings/EnvironmentName";
import { hasPermission } from "utils/authService";
import { PermissionSetMap } from "constants/RBACConstants";
import { useHasDisabledEnvironment } from "contexts/GlobalSettingsContext";

const RepoMappingForm = forwardRef( ({afterClose, configData, installation, hostUrl, ...props}, ref) => {
    const [form] = Form.useForm();
    const [data, setData] = useState(() => getInitialData(configData));
    const [envList, setEnvList] = useState([])
    const { id } = configData || {}
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
    const fetchEnv = useCallback((id) => {
        const {promise, cancel} = SubProductEnvironmentTypeAPI.getAllQuick(id);
        promise.then(setEnvList);
        return cancel
    }, [])

    useEffect(() => {
        if(data.subProductId) {
            return fetchEnv(data.subProductId)
        }
    }, [data.subProductId, fetchEnv])

    const submitData = async () => {
        await form.validateFields();
        if(hasDisabledEnvironment){
            data.envId = _.find(envList, (env) => env.name === envValue)?.id;
        }
        const envData = _.find(envList, (env) => env.id === data.envId);
        delete data.productId 
        await ToolsIntegrationsAPI.createGitMapping({...data, repoId: id, envName: envData.name, ...(!_.isUndefined(hostUrl) && { hostUrl})}).promise
        message.success(`Mapping has been successfully saved`);
    }

    const baseModalProps = {
        okText: "Save",
        disabled: !hasPermission(PermissionSetMap.EDIT_TOOL_CONFIG)
    }
    const installationID = installation?.id || configData.gitInstallation?.id ;
    const branchAPIParams = useMemo(() => ({
        type: configData.repositoryType,
        repository: configData.name,
        configId: installationID,
        repoId: id,
        ...(!_.isUndefined(hostUrl) && { hostUrl})
    }), [configData.name, installationID, hostUrl, configData.repositoryType, id]);

    const getBranches = useCallback((params) => {
        const {promise, cancel} = RepositoryAPI.getBranches({...params, ...branchAPIParams}, true);
        return {promise, cancel}
    }, [branchAPIParams])

    const fields = [
        {
            name: "productId",
            label: <Vocabulary name="product" />,
            type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
            colSpan: 24,
            showSearch: true,
            required: true,
            props: {
                dropdownType: 'product',
                showAddButton: true,
                onChange: (e) => form.setFieldsValue({subProductId: null, envId: null}),
                onCreate: (createdProduct) => {
                    form.setFieldsValue({
                        productId: createdProduct.id,
                        subProductId: null,
                        envId: null
                    });
                    setData({ ...data, productId: createdProduct.id, subProductId: null, envId: null })
                },
                autoFocus: true
            }
        },
        {
            name: "subProductId",
            label: <Vocabulary name="subProduct" />,
            type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
            colSpan: 24,
            showSearch: true,
            required: true,
            props: {
                productId: data.productId,
                showAddButton: !!data.productId,
                dropdownType: 'subProduct',
                onChange: (e) => form.setFieldsValue({envId: null}),
                onCreate: (createdSubproduct) => {
                    form.setFieldsValue({
                        subProductId: createdSubproduct.id,
                        envId: null
                    });
                    setData({ ...data, subProductId: createdSubproduct.id, envId: null })
                }
            }
        },
        ...(hasDisabledEnvironment ? [] : [{
            name: "envId",
            label: "Environment",
            type: FieldTypes.SELECT,
            colSpan: 24,
            showSearch: true,
            required: true,
            options: envList.map((env) => ({
                label: <EnvironmentName name={env.name} />,
                value: env.id,
              }))
        }]),
        {
            label: "Branch Name",
            name: "branchName",
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            required: true,
            notFoundContent: "No branches defined for this repository",
            props: {
                api: getBranches,
                showDefaultError: false
            }
        }
    ]

    const onValuesChange = useCallback( (_, values) => {
        setData({...data, ...values})
    }, [data])

    return (
        <BaseModalForm             
            modalProps={baseModalProps}
            ref={ref}
            afterClose={afterClose}
            form={form}
            fields={fields}
            initialValues={data}
            onValuesChange={onValuesChange}
            onOk={submitData}
            title={configData.productId ? `Edit ${configData.name || ''} repository details`: `Map ${configData.name || ''} repository details`}
        >
        </BaseModalForm>
    )
})

export default RepoMappingForm


function getInitialData(data = {}) {
    if(_.isEmpty(data)) {
        return data
    }
    data.envId = data.environmentId
    if(data.branch) {
        data.branchName = data.branch
    }
    return _.pick(data, "productId", "subProductId", "envId", "branchName")
}